import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Users, Smartphone, Camera, TrendingUp,
  AlertCircle, Activity, CheckCircle
} from 'lucide-react';
import { message, Spin } from 'antd';
import axios from "../../utils/axios";
import { MdOutlineCrisisAlert } from "react-icons/md";
import { useLocation } from "react-router-dom";

const ModernCafeDashboard = () => {
  const cafeId = localStorage.getItem('cafeId');
  const user = JSON.parse(localStorage.getItem('user'));
  const organizationId = localStorage.getItem('organizationId');
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [currentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const [peopleCount, setPeopleCount] = useState(0);
  const [mobileUsage] = useState(68);
  const [alerts, setAlerts] = useState([]);
  const [alertsCount, setAlertsCount] = useState(0);
  const [peopleCountData, setPeopleCountData] = useState([]);
  const [cameraCount, setCameraCount] = useState(0);
  const [tableOcc, setTableOcc] = useState([]);

  const location = useLocation();
  const projectname = location.state?.projectname || localStorage.getItem('selectedProject');

  /* ---------------- DASHBOARD CONFIG ---------------- */
  const fetchDashboardConfig = async () => {
    try {
      const res = await axios.post('/fetch-dashboard-settings', {
        organizationId,
        username: user?.username,
        projectname
      });

      if (res.data?.success) {
        setDashboardConfig(res.data.data.pages?.dashboard);
      }
    } catch (err) {
      console.error("Dashboard config fetch failed", err);
    }
  };

  /* ---------------- DATA FETCH ---------------- */
  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await axios.post("/get-recent-alerts", { cafeId });

      if (res?.data?.alerts) {
        setAlerts(res.data.alerts.map((a, i) => ({
          key: i,
          type: a.alertType,
          camera: a.cameraId,
          time: new Date(a.createdOn).toLocaleString(),
          severity: (a.severity || '').toLowerCase()
        })));
      }

      if (res?.data?.total) setAlertsCount(res.data.total);
    } catch {
      message.error("Error fetching alerts");
    } finally {
      setLoading(false);
    }
  };

  const getPersonCount = async () => {
    const res = await axios.post("/get-person-count", { cafeId });
    if (res?.data?.personCount) setPeopleCount(res.data.personCount);
  };

  const getCustomerCountGraphData = async () => {
    const res = await axios.post("/get-customer-count-graph-data", { cafeId });
    if (res?.data?.data) setPeopleCountData(res.data.data);
  };

  const getCameraCount = async () => {
    const res = await axios.post("/get-camera-count", { cafeId });
    if (res?.data?.cameraCount) setCameraCount(res.data.cameraCount);
  };

  const getTableOccupancyData = async () => {
    const res = await axios.post("/get-table-occupancy-data", { cafeId });
    if (res?.data?.tables) setTableOcc(res.data.tables);
  };

  useEffect(() => {
    fetchDashboardConfig();
    fetchAlerts();
    getPersonCount();
    getCustomerCountGraphData();
    getCameraCount();
    getTableOccupancyData();
  }, []);

  /* ---------------- COMPONENTS (UNCHANGED) ---------------- */
  const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
      </div>
      <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  );

  const TableCard = ({ tableId, occupancy, capacity }) => {
    const percent = (occupancy / capacity) * 100;
    const color =
      percent === 0 ? "bg-emerald-500" :
      percent < 100 ? "bg-amber-500" : "bg-red-500";

    return (
      <div className="relative rounded-xl border-2 bg-white p-6 hover:shadow-lg transition">
        <h3 className="text-2xl font-bold mb-2">Table {tableId}</h3>
        <p className="text-xl font-semibold">{occupancy}/{capacity}</p>
        <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
          <div className={`${color} h-full rounded-full`} style={{ width: `${percent}%` }} />
        </div>
      </div>
    );
  };

  const CARD_RENDERERS = {
  "Live Occupancy": {
    span: 1, // 4 per row
    render: () => (
      <MetricCard
        title="Live Occupancy"
        value={peopleCount}
        subtitle="Current visitors"
        icon={Users}
        color="from-blue-500 to-blue-600"
      />
    )
  },

  "Live Count": {
    span: 1,
    render: () => (
      <MetricCard
        title="Live Count"
        value={peopleCount}
        subtitle="Current visitors"
        icon={Users}
        color="from-blue-500 to-blue-600"
      />
    )
  },

  "System Status": {
    span: 1,
    render: () => (
      <MetricCard
        title="System Status"
        value={"Online"}
        subtitle="Operational"
        icon={CheckCircle}
        color="from-emerald-500 to-emerald-600"
      />
    )
  },

  "Violation Count": {
    span: 1,
    render: () => (
      <MetricCard
        title="Violation Count"
        value={alertsCount}
        subtitle="Live alert count"
        icon={MdOutlineCrisisAlert}
        color="from-rose-500 to-rose-600"
      />
    )
  },

  "Mobile Usage": {
    span: 1,
    render: () => (
      <MetricCard
        title="Mobile Usage"
        value={`${mobileUsage}%`}
        subtitle="Customers on devices"
        icon={Smartphone}
        color="from-purple-500 to-purple-600"
      />
    )
  },

  "Total Alerts": {
    span: 1,
    render: () => (
      <MetricCard
        title="Total Alerts"
        value={alertsCount}
        subtitle="Live alert count"
        icon={MdOutlineCrisisAlert}
        color="from-rose-500 to-rose-600"
      />
    )
  },

  "Active Cameras": {
    span: 1,
    render: () => (
      <MetricCard
        title="Active Cameras"
        value={`${cameraCount}/${cameraCount}`}
        subtitle="Operational"
        icon={Camera}
        color="from-emerald-500 to-emerald-600"
      />
    )
  },

  "Restaurant Tables": {
    span: 4, // full width
    render: () => (
      <div className="bg-white p-8">
        <h1 className="text-4xl font-bold mb-8">Restaurant Tables</h1>
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {tableOcc.map(t => <TableCard key={t.tableId} {...t} />)}
        </div>
      </div>
    )
  },

  "Crowd Levels": {
    span: 2, // half width
    render: () => (
      <div className="bg-white rounded-2xl p-6 shadow-sm border">
        <h3 className="flex items-center gap-2 font-semibold mb-4">
          <TrendingUp /> Crowd Levels
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="time" />
            <YAxis />
            <Tooltip />
            <Area dataKey="count" stroke="#6366f1" fill="#c7d2fe" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )
  },

  "Table Occupancy %": {
    span: 2,
    render: () => (
      <div className="bg-white rounded-2xl p-6 shadow-sm border">
        <h3 className="font-semibold mb-4">Table Occupancy %</h3>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={tablePieData} dataKey="value" cx="50%" cy="50%" innerRadius={70} outerRadius={100}>
              {tablePieData.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    )
  },

  "Recent Alerts": {
    span: 4, // full width
    render: () => (
      <div className="bg-white rounded-2xl p-6 shadow-sm border">
        <h3 className="flex items-center gap-2 font-semibold mb-4">
          <AlertCircle /> Recent Alerts
        </h3>
        {loading ? <Spin /> : alerts.map(a => (
          <div key={a.key} className="p-3 bg-gray-50 rounded mb-2">
            <strong>{a.type}</strong> • {a.camera} • {a.time}
          </div>
        ))}
      </div>
    )
  }
};


const getGridColsClass = (cards = []) => {
  const count = cards.length;

  if (count === 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  return "grid-cols-4"; // 3 or more
};



  const graphData = peopleCountData
    .slice()
    .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn))
    .map(i => ({
      time: new Date(i.createdOn).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit"
      }),
      count: i.personCount
    }));

  const tablePieData = (() => {
    let occ = 0, cap = 0;
    tableOcc.forEach(t => { occ += t.occupancy; cap += t.capacity; });
    return [
      { name: "Occupied", value: occ, color: "#ef4444" },
      { name: "Available", value: cap - occ, color: "#10b981" }
    ];
  })();

  /* ---------------- UI (UNCHANGED) ---------------- */
  return (
    <div className="min-h-screen max-w-7xl mx-auto">

      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Vision AI Analytics
        </h1>
        <p className="text-gray-600 flex items-center gap-2">
          <Activity className="w-4 h-4" /> {currentTime.toLocaleString()}
        </p>
      </div>

      {dashboardConfig?.rows?.map((row, rowIdx) => (
  <div
    key={rowIdx}
    className={`grid ${getGridColsClass(row.cards)} gap-6 mb-6`}
  >
    {row.cards.map((card, idx) => {
      const def = CARD_RENDERERS[card.name];
      if (!def) return null;

      return (
        <div key={idx}>
          {def.render()}
        </div>
      );
    })}
  </div>
))}



      {/* METRICS */}
      {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {isCardVisible("Live Occupancy") && (
          <MetricCard title="Live Occupancy" value={peopleCount} subtitle="Current visitors" icon={Users} color="from-blue-500 to-blue-600" />
        )}
        {isCardVisible("Mobile Usage") && (
          <MetricCard title="Mobile Usage" value={`${mobileUsage}%`} subtitle="Customers on devices" icon={Smartphone} color="from-purple-500 to-purple-600" />
        )}
        {isCardVisible("Total Alerts") && (
          <MetricCard title="Total Alerts" value={alertsCount} subtitle="Live alert count" icon={MdOutlineCrisisAlert} color="from-rose-500 to-rose-600" />
        )}
        {isCardVisible("Active Cameras") && (
          <MetricCard title="Active Cameras" value={`${cameraCount}/${cameraCount}`} subtitle="Operational" icon={Camera} color="from-emerald-500 to-emerald-600" />
        )}
      </div> */}

      {/* RESTAURANT TABLES */}
      {/* {isCardVisible("Restaurant Tables") && (
        <div className="bg-white p-8 mb-4">
          <h1 className="text-4xl font-bold mb-8">Restaurant Tables</h1>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {tableOcc.map(t => <TableCard key={t.tableId} {...t} />)}
          </div>
        </div>
      )} */}

      {/* CROWD LEVELS */}
      {/* {isCardVisible("Crowd Levels") && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border mb-6">
          <h3 className="flex items-center gap-2 font-semibold mb-4">
            <TrendingUp /> Crowd Levels
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={graphData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Area dataKey="count" stroke="#6366f1" fill="#c7d2fe" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* TABLE OCCUPANCY PIE */}
      {/* {isCardVisible("Table Occupancy %") && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="font-semibold mb-4">Table Occupancy %</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={tablePieData} dataKey="value" cx="50%" cy="50%" innerRadius={70} outerRadius={100}>
                {tablePieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )} */}

      {/* RECENT ALERTS */}
      {/* {isCardVisible("Recent Alerts") && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border mt-6">
          <h3 className="flex items-center gap-2 font-semibold mb-4">
            <AlertCircle /> Recent Alerts
          </h3>
          {loading ? <Spin /> : alerts.map(a => (
            <div key={a.key} className="p-3 bg-gray-50 rounded mb-2">
              <strong>{a.type}</strong> • {a.camera} • {a.time}
            </div>
          ))}
        </div>
      )} */}

    </div>
  );
};

export default ModernCafeDashboard;